import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useOpportunitiesStore } from "./opportunitiesStore";
import { useLiveBoardSync } from "./useLiveBoardSync";
import { insertIdAtTop, removeId, type ColumnState } from "./liveBoardSync";
import { matchesBoardFilters } from "./opportunityFilters";
import { useOpportunityStagesStore } from "./opportunityStagesStore";
import {
  useAuthStore,
  useHasAnyPermission,
  useHasPermission,
} from "@/store/authStore";
import { useTeamsStore } from "@/store/teamsStore";
import { useLocationsStore } from "@/modules/locations/locationsStore";
import {
  useQuoteCustomFieldNames,
  useShowLocationField,
} from "@/store/quoteSettingsStore";
import { useOpportunitySettingsStore } from "@/store/opportunitySettingsStore";
import { useCompanyCustomFieldNames } from "@/store/companySettingsStore";
import {
  getFilterableCustomFields,
  pruneCustomFieldFilter,
} from "@/lib/customFieldFilters";
import {
  CreateOpportunityParams,
  UpdateOpportunityParams,
  OpportunityBoardFilters,
  Permission,
  getCustomFieldNames,
} from "@forgepoint/shared";
import { toast } from "sonner";
import { OpportunityDetailSheet } from "./OpportunityDetailSheet";
import { OpportunitySearchBar } from "./OpportunitySearchBar";
import { OpportunityFilterBar } from "./OpportunityFilterBar";
import type {
  OpportunityArchiveMode,
  OpportunityCreatedRange,
} from "./opportunityFilterTypes";
import { resolveCreatedRange } from "./createdRange";
import { readBoardFilters, writeBoardFilters } from "./boardFilterStorage";
import { KanbanBoard } from "@/components/kanban/KanbanBoard";
import { OpportunityKanbanCard } from "./OpportunityKanbanCard";
import { OpportunityFieldVisibilityMenu } from "./OpportunityFieldVisibilityMenu";
import { useOpportunityCardFields } from "./useOpportunityCardFields";
import { OpportunityStatistics } from "./OpportunityStatistics";
import { useNavigate, useParams } from "react-router";
import { StageRequiredFieldsChip } from "./components/StageRequiredFieldsChip";
import { getStageRequiredLabels } from "./stageRequirements";
import { showRefusedStageMove } from "./refusedStageMove";

const COLUMN_PAGE_SIZE = 25;

/**
 * Folds the archive view and the created-date selection into the person and
 * location filters, so all of them travel together to the server and to the
 * client-side matcher. Resolve per use: a preset's window is measured from the
 * call time.
 * @param filters - The active person and location selections
 * @param mode - The archive view: Active, Archived, or All
 * @param createdRange - The created-date selection, or null when off
 * @returns The board filters with the archive flags and created bounds applied
 */
function withViewFilters(
  filters: OpportunityBoardFilters,
  mode: OpportunityArchiveMode,
  createdRange: OpportunityCreatedRange | null,
): OpportunityBoardFilters {
  return {
    ...filters,
    ...resolveCreatedRange(createdRange, new Date()),
    includeArchived: mode !== "active",
    archivedOnly: mode === "archived",
  };
}

export default function OpportunitiesController() {
  const navigate = useNavigate();
  const { opportunityId: editingOpportunityId } = useParams<{
    opportunityId?: string;
  }>();
  const {
    opportunities,
    statistics,
    statisticsLoading,
    peopleFilterCounts,
    fetchOpportunities,
    fetchOpportunityStatistics,
    fetchOpportunityPeopleFilterCounts,
    createOpportunity,
    updateOpportunity,
    archiveOpportunity,
    unarchiveOpportunity,
  } = useOpportunitiesStore();

  const canCreateOpportunities = useHasAnyPermission(
    Permission.OPPORTUNITIES_EDIT_OWN,
    Permission.OPPORTUNITIES_EDIT_ALL,
  );

  const fetchUsers = useTeamsStore((state) => state.fetchUsers);
  const fetchLocations = useLocationsStore((state) => state.fetchLocations);
  const canViewLocations = useHasPermission(Permission.LOCATIONS_VIEW);
  const showLocationField = useShowLocationField();
  const quoteCustomFieldNames = useQuoteCustomFieldNames();
  const companyCustomFieldNames = useCompanyCustomFieldNames();
  const customFieldsSchema = useOpportunitySettingsStore(
    (s) => s.settings?.customFieldsSchema ?? null,
  );
  const settingsLoaded = useOpportunitySettingsStore(
    (s) => s.settings !== null,
  );
  const fetchOpportunitySettings = useOpportunitySettingsStore(
    (s) => s.fetchSettings,
  );
  const { visibleFieldIds, toggleField } = useOpportunityCardFields();
  const customFieldNames = useMemo(
    () => getCustomFieldNames(customFieldsSchema),
    [customFieldsSchema],
  );
  const filterableCustomFields = useMemo(
    () => getFilterableCustomFields(customFieldsSchema),
    [customFieldsSchema],
  );
  const {
    getActiveStages,
    getStage,
    fetchStages,
    stages,
    hasFetched: stagesHasFetched,
  } = useOpportunityStagesStore();

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchOpportunitySettings();
  }, [fetchOpportunitySettings]);

  useEffect(() => {
    if (showLocationField && canViewLocations) {
      fetchLocations();
    }
  }, [showLocationField, canViewLocations, fetchLocations]);

  useEffect(() => {
    if (!stagesHasFetched) {
      fetchStages();
    }
  }, [stagesHasFetched, fetchStages]);

  const userId = useAuthStore((state) => state.user?.id);
  const storedFilters = useMemo(() => readBoardFilters(userId), [userId]);

  const [archiveMode, setArchiveMode] = useState<OpportunityArchiveMode>(
    () => storedFilters.archiveMode,
  );
  const [boardFilters, setBoardFilters] = useState<OpportunityBoardFilters>(
    () => storedFilters.boardFilters,
  );
  const [createdRange, setCreatedRange] =
    useState<OpportunityCreatedRange | null>(() => storedFilters.createdRange);
  const [searchValue, setSearchValue] = useState("");
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [stageEditTarget, setStageEditTarget] = useState<{
    opportunityId: string;
    initialEditStageId: string;
  } | null>(null);
  const [columnStates, setColumnStates] = useState<Map<string, ColumnState>>(
    new Map(),
  );
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasInitiallyFetchedRef = useRef(false);
  const archiveModeRef = useRef(archiveMode);
  archiveModeRef.current = archiveMode;
  const boardFiltersRef = useRef(boardFilters);
  boardFiltersRef.current = boardFilters;
  const createdRangeRef = useRef(createdRange);
  createdRangeRef.current = createdRange;
  const columnStatesRef = useRef(columnStates);
  columnStatesRef.current = columnStates;

  /** The selections as the server and the client-side matcher both see them. */
  const viewFiltersRef = useRef(boardFilters);
  viewFiltersRef.current = withViewFilters(
    boardFilters,
    archiveMode,
    createdRange,
  );

  const kanbanStatuses = useMemo(
    () =>
      getActiveStages().map((stage) => ({
        id: stage.id,
        title: stage.name,
        badge: (
          <StageRequiredFieldsChip
            labels={getStageRequiredLabels(
              stage.requiredFields,
              customFieldsSchema,
            )}
          />
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stages, customFieldsSchema],
  );

  /**
   * Fetches all columns in parallel, one request per stage.
   * Resets offsets and replaces column IDs. The archive view is read from the
   * current archive mode.
   * @param search - Optional search term
   * @param refreshFilterCounts - Whether to also refresh the per-person filter
   *   counts. Skip it when only the person selections changed, since the counts
   *   depend on search, archive view, location, and created range, not on
   *   which people are selected.
   */
  const fetchAllColumns = useCallback(
    async (search?: string, refreshFilterCounts = true) => {
      const activeStages = getActiveStages();
      if (activeStages.length === 0) {
        return;
      }

      const filters = withViewFilters(
        boardFiltersRef.current,
        archiveModeRef.current,
        createdRangeRef.current,
      );

      if (refreshFilterCounts) {
        fetchOpportunityPeopleFilterCounts(search, filters);
      }

      setIsInitialLoad((prev) => (columnStates.size === 0 ? true : prev));

      // Mark all columns as loading
      setColumnStates((prev) => {
        const next = new Map(prev);
        for (const stage of activeStages) {
          const existing = next.get(stage.id);
          next.set(stage.id, {
            ids: existing?.ids ?? [],
            total: existing?.total ?? 0,
            isLoading: true,
          });
        }
        return next;
      });

      const [results] = await Promise.all([
        Promise.all(
          activeStages.map(async (stage) => {
            const result = await fetchOpportunities(search, {
              limit: COLUMN_PAGE_SIZE,
              offset: 0,
              stageIds: [stage.id],
              ...filters,
            });
            return { stageId: stage.id, ids: result.ids, total: result.total };
          }),
        ),
        fetchOpportunityStatistics(search, filters),
      ]);

      setColumnStates(() => {
        const next = new Map<string, ColumnState>();
        for (const { stageId, ids, total } of results) {
          next.set(stageId, { ids, total, isLoading: false });
        }
        return next;
      });
      setIsInitialLoad(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fetchOpportunities,
      fetchOpportunityStatistics,
      fetchOpportunityPeopleFilterCounts,
      columnStates.size,
      stages,
    ],
  );

  // Initial fetch — wait for stages to load before fetching columns
  useEffect(() => {
    if (!hasInitiallyFetchedRef.current && stagesHasFetched) {
      hasInitiallyFetchedRef.current = true;
      fetchAllColumns(undefined);
    }
  }, [stagesHasFetched, fetchAllColumns]);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Builds the flat array of opportunities from all column states for the KanbanBoard.
   */
  const opportunitiesArray = useMemo(() => {
    const allIds: string[] = [];
    for (const [, state] of columnStates) {
      if (archiveMode !== "active") {
        // Sort archived opportunities to the top of each column
        const sorted = [...state.ids].sort((a, b) => {
          const aArchived = opportunities.get(a)?.archivedAt ? 1 : 0;
          const bArchived = opportunities.get(b)?.archivedAt ? 1 : 0;
          return bArchived - aArchived;
        });
        allIds.push(...sorted);
      } else {
        allIds.push(...state.ids);
      }
    }
    return allIds
      .map((id) => {
        const opp = opportunities.get(id);
        if (!opp) {
          return undefined;
        }
        // KanbanBoard groups items by `status` field — override it with stageId
        // so items land in the correct stage column
        return { ...opp, status: opp.stageId ?? opp.status };
      })
      .filter((opp): opp is NonNullable<typeof opp> => opp !== undefined);
  }, [columnStates, opportunities, archiveMode]);

  const columnMeta = useMemo(() => {
    const meta = new Map<string, { total: number; isLoadingMore: boolean }>();
    for (const [stageId, state] of columnStates) {
      meta.set(stageId, {
        total: state.total,
        isLoadingMore: state.isLoading && state.ids.length > 0,
      });
    }
    return meta;
  }, [columnStates]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      fetchAllColumns(value.trim() || undefined);
    }, 300);
  };

  const handleCreate = () => {
    setIsCreateSheetOpen(true);
  };

  const handleOpportunityClick = (id: string) => {
    navigate(`/opportunities/${id}`);
  };

  const handleEditSheetOpenChange = (open: boolean) => {
    if (open) {
      return;
    }

    setStageEditTarget(null);
    navigate("/opportunities", { replace: true });
  };

  /**
   * Opens an opportunity's sheet in edit mode with a stage preselected.
   * @param opportunityId - The opportunity to edit
   * @param stageId - The stage to preselect
   * @returns Nothing
   */
  const openEditorOnStage = (opportunityId: string, stageId: string) => {
    setStageEditTarget({ opportunityId, initialEditStageId: stageId });
    navigate(`/opportunities/${opportunityId}`);
  };

  /**
   * Refreshes the statistics bar for the current search, board filters, and
   * archive view. Called after any mutation that changes the visible set.
   */
  const refreshStatistics = () => {
    fetchOpportunityStatistics(
      searchValue || undefined,
      withViewFilters(
        boardFiltersRef.current,
        archiveModeRef.current,
        createdRangeRef.current,
      ),
    );
  };

  /**
   * Refreshes the per-person filter counts for the current search and archive
   * view. Called after mutations that change a person assignment or which
   * opportunities are in the active set, so the counts in the filter dropdowns
   * stay accurate.
   */
  const refreshFilterCounts = () => {
    fetchOpportunityPeopleFilterCounts(
      searchValue || undefined,
      withViewFilters(
        boardFiltersRef.current,
        archiveModeRef.current,
        createdRangeRef.current,
      ),
    );
  };

  const { handleDragActiveChange } = useLiveBoardSync({
    hasInitiallyFetchedRef,
    columnStatesRef,
    viewFiltersRef,
    archiveModeRef,
    searchValue,
    setColumnStates,
    fetchAllColumns,
    refreshStatistics,
    refreshFilterCounts,
  });

  const handleSaveCreate = async (
    data: CreateOpportunityParams,
  ): Promise<true | string> => {
    const { opportunity, error: createError } = await createOpportunity(data);
    if (opportunity) {
      const columnKey = opportunity.stageId ?? opportunity.status;
      // A brand-new opportunity has no tasks yet, so it can't match a
      // task-assignee filter; otherwise only show it when it matches.
      const filters = viewFiltersRef.current;
      const showOptimistically =
        !filters.taskAssigneeUserIds?.length &&
        matchesBoardFilters(opportunity, filters);
      if (showOptimistically) {
        setColumnStates((prev) => {
          const next = new Map(prev);
          const col = next.get(columnKey);
          if (col) {
            next.set(columnKey, insertIdAtTop(col, opportunity.id));
          }
          return next;
        });
      }
      refreshStatistics();
      refreshFilterCounts();
      return true;
    }
    return createError ?? "Failed to create opportunity";
  };

  const handleSaveEdit = async (
    data: UpdateOpportunityParams,
  ): Promise<true | string> => {
    if (!editingOpportunityId) {
      return "No opportunity selected";
    }
    const { opportunity, error: updateError } = await updateOpportunity(
      editingOpportunityId,
      data,
    );
    if (opportunity) {
      const newColumnKey = opportunity.stageId ?? opportunity.status;
      // Enforce the confirmed placement with the same sweep as drag moves
      // (see handleStageChange): drop the card everywhere but its column, and
      // keep it there only when it still matches the active filters and was
      // already on the board (an edit must not materialize a card that
      // pagination or filters had excluded).
      const belongs = matchesBoardFilters(opportunity, viewFiltersRef.current);
      setColumnStates((prev) => {
        const next = new Map(prev);
        const wasOnBoard = Array.from(prev.values()).some((col) =>
          col.ids.includes(editingOpportunityId),
        );
        for (const [key, col] of prev) {
          if (key !== newColumnKey) {
            next.set(key, removeId(col, editingOpportunityId));
          }
        }
        const target = next.get(newColumnKey);
        if (target) {
          next.set(
            newColumnKey,
            belongs && wasOnBoard
              ? insertIdAtTop(target, editingOpportunityId)
              : removeId(target, editingOpportunityId),
          );
        }
        return next;
      });
      refreshStatistics();
      refreshFilterCounts();
      return true;
    }
    return updateError ?? "Failed to update opportunity";
  };

  /**
   * Archives an opportunity and updates the board.
   * When archived items are hidden the card is removed optimistically; when
   * they are shown the card stays in place and re-sorts as archived, driven by
   * the store's updated record.
   * @param id - The id of the opportunity to archive
   */
  const handleArchive = async (id: string) => {
    const opportunity = opportunities.get(id);
    if (!opportunity) {
      return;
    }
    const columnKey = opportunity.stageId ?? opportunity.status;
    const removeOptimistically = archiveModeRef.current === "active";

    // Optimistically remove from column when archived items are hidden
    if (removeOptimistically) {
      setColumnStates((prev) => {
        const next = new Map(prev);
        const col = next.get(columnKey);
        if (col) {
          next.set(columnKey, removeId(col, id));
        }
        return next;
      });
    }

    const result = await archiveOpportunity(id);
    if (!result.success) {
      // Revert on failure
      if (removeOptimistically) {
        setColumnStates((prev) => {
          const next = new Map(prev);
          const col = next.get(columnKey);
          if (col) {
            next.set(columnKey, insertIdAtTop(col, id));
          }
          return next;
        });
      }
      toast.error("Failed to archive opportunity");
      return;
    }
    toast.success("Opportunity archived");
    refreshStatistics();
    refreshFilterCounts();
  };

  const handleUnarchive = async (id: string) => {
    const opportunity = opportunities.get(id);
    if (!opportunity) {
      return;
    }

    const result = await unarchiveOpportunity(id);
    if (!result.success) {
      toast.error("Failed to unarchive opportunity");
      return;
    }
    toast.success("Opportunity unarchived");
    await fetchAllColumns(searchValue || undefined);
  };

  /** Saves the selections as the refs now hold them, so a refresh restores them. */
  const persistFilters = () => {
    writeBoardFilters(userId, {
      archiveMode: archiveModeRef.current,
      boardFilters: boardFiltersRef.current,
      createdRange: createdRangeRef.current,
    });
  };

  const handleArchiveModeChange = async (mode: OpportunityArchiveMode) => {
    setArchiveMode(mode);
    archiveModeRef.current = mode;
    persistFilters();
    await fetchAllColumns(searchValue || undefined);
  };

  const handleFiltersChange = async (filters: OpportunityBoardFilters) => {
    // Counts are company-, location-, and custom-field-scoped, so only a change
    // to those selections makes them stale.
    const countsScopeChanged =
      boardFiltersRef.current.companyIds?.join() !==
        filters.companyIds?.join() ||
      boardFiltersRef.current.locationIds?.join() !==
        filters.locationIds?.join() ||
      JSON.stringify(boardFiltersRef.current.customFields) !==
        JSON.stringify(filters.customFields);
    setBoardFilters(filters);
    boardFiltersRef.current = filters;
    persistFilters();
    await fetchAllColumns(searchValue || undefined, countsScopeChanged);
  };

  // A stored selection for a field that was deleted or changed type would
  // otherwise hide every card with no chip to clear it.
  useEffect(() => {
    if (!settingsLoaded) {
      return;
    }
    const current = boardFiltersRef.current.customFields;
    const pruned = pruneCustomFieldFilter(current, filterableCustomFields);
    if (
      Object.keys(pruned ?? {}).length === Object.keys(current ?? {}).length
    ) {
      return;
    }
    handleFiltersChange({ ...boardFiltersRef.current, customFields: pruned });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsLoaded, filterableCustomFields]);

  const handleCreatedRangeChange = async (
    range: OpportunityCreatedRange | null,
  ) => {
    setCreatedRange(range);
    createdRangeRef.current = range;
    persistFilters();
    await fetchAllColumns(searchValue || undefined);
  };

  // Resets every selection before fetching, so one request replaces the board.
  const handleClearFilters = async () => {
    setBoardFilters({});
    boardFiltersRef.current = {};
    setArchiveMode("active");
    archiveModeRef.current = "active";
    setCreatedRange(null);
    createdRangeRef.current = null;
    persistFilters();
    await fetchAllColumns(searchValue || undefined);
  };

  /**
   * Handles drag-and-drop between kanban columns by updating the opportunity's stageId.
   * @param opportunityId - The opportunity being moved
   * @param newStageId - The target stage ID (column)
   * @returns True when the move was saved, false when it was refused
   */
  const handleStageChange = async (
    opportunityId: string,
    newStageId: string,
  ): Promise<boolean> => {
    const { opportunity, error, missingFields } = await updateOpportunity(
      opportunityId,
      { stageId: newStageId },
    );

    if (!opportunity) {
      showRefusedStageMove({
        stageName: getStage(newStageId)?.name,
        error,
        missingFields,
        onFillInFields: () => openEditorOnStage(opportunityId, newStageId),
      });
      return false;
    }

    // Enforce the confirmed placement: remove the id from every other column
    // and insert it into the target. Deriving the source column from the
    // possibly-stale record misfired on rapid drag-backs (the old-stage guess
    // equalled the target, so the arrays were never updated), stranding the
    // id in the wrong column and duplicating it on a later move. The sweep is
    // order-independent under interleaved in-flight moves and also cleans up
    // any previously drifted state; the idempotent helpers make it compose
    // with the live delta poll echoing this client's own update back.
    setColumnStates((prev) => {
      const next = new Map(prev);
      for (const [key, col] of prev) {
        if (key !== newStageId) {
          next.set(key, removeId(col, opportunityId));
        }
      }
      const target = next.get(newStageId);
      if (target) {
        next.set(newStageId, insertIdAtTop(target, opportunityId));
      }
      return next;
    });
    refreshStatistics();
    return true;
  };

  /**
   * Loads the next page of items for a specific stage column.
   * @param stageId - The stage ID column to load more items for
   */
  const handleLoadMore = useCallback(
    async (stageId: string) => {
      const current = columnStates.get(stageId);
      if (
        !current ||
        current.isLoading ||
        current.ids.length >= current.total
      ) {
        return;
      }

      setColumnStates((prev) => {
        const next = new Map(prev);
        const col = next.get(stageId);
        if (col) {
          next.set(stageId, { ...col, isLoading: true });
        }
        return next;
      });

      const result = await fetchOpportunities(searchValue || undefined, {
        limit: COLUMN_PAGE_SIZE,
        offset: current.ids.length,
        stageIds: [stageId],
        ...withViewFilters(
          boardFiltersRef.current,
          archiveModeRef.current,
          createdRangeRef.current,
        ),
      });

      setColumnStates((prev) => {
        const next = new Map(prev);
        const col = next.get(stageId);
        if (col) {
          // Dedupe against ids the live delta poll may have inserted since
          // the page request was issued.
          next.set(stageId, {
            ids: [
              ...col.ids,
              ...result.ids.filter((id) => !col.ids.includes(id)),
            ],
            total: result.total,
            isLoading: false,
          });
        }
        return next;
      });
    },
    [columnStates, fetchOpportunities, searchValue],
  );

  if ((isInitialLoad && columnStates.size === 0) || !stagesHasFetched) {
    return (
      <div className="flex flex-col flex-1 min-h-0">
        <div className="flex flex-col gap-3 flex-shrink-0 px-4 pt-3">
          <div className="flex items-center justify-between gap-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-8 w-40" />
          </div>
          <Skeleton className="h-8 w-32" />
        </div>
        <div className="pt-3">
          <div className="flex items-center gap-3 px-4 text-sm">
            <Skeleton className="h-6 w-20" />
            <span className="text-border">·</span>
            <Skeleton className="h-6 w-20" />
            <span className="text-border">·</span>
            <Skeleton className="h-6 w-16" />
          </div>
        </div>
        <div className="flex-1 relative min-h-0">
          <div className="absolute inset-0 overflow-x-auto overflow-y-hidden">
            <div className="flex gap-3 p-3 h-full animate-stagger">
              {kanbanStatuses.map((status) => (
                <div
                  key={status.id}
                  className="flex flex-col flex-1 min-w-[200px] max-w-[400px] bg-muted/30 rounded-lg p-3 h-full"
                >
                  <div className="flex items-center justify-between mb-2">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-3 w-4" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-16 w-full rounded-lg" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex flex-col gap-3 flex-shrink-0 px-4 pt-3">
        <div className="flex items-center justify-between gap-4">
          <OpportunitySearchBar
            value={searchValue}
            onChange={handleSearchChange}
            isLoading={isInitialLoad}
          />
          <div className="flex items-center gap-2">
            <OpportunityFieldVisibilityMenu
              visibleFieldIds={visibleFieldIds}
              onToggle={toggleField}
              customFieldNames={customFieldNames}
              quoteCustomFieldNames={quoteCustomFieldNames}
              companyCustomFieldNames={companyCustomFieldNames}
            />
            {canCreateOpportunities && (
              <Button onClick={handleCreate}>Create Opportunity</Button>
            )}
          </div>
        </div>
        <OpportunityFilterBar
          value={boardFilters}
          onChange={handleFiltersChange}
          filterCounts={peopleFilterCounts}
          archiveMode={archiveMode}
          onArchiveModeChange={handleArchiveModeChange}
          createdRange={createdRange}
          onCreatedRangeChange={handleCreatedRangeChange}
          onClearAll={handleClearFilters}
          // A selection that outlived the setting or the viewer's access stays clearable.
          showLocationFilter={
            (showLocationField && canViewLocations) ||
            (boardFilters.locationIds?.length ?? 0) > 0
          }
          customFields={filterableCustomFields}
        />
      </div>

      <div className="pt-3 flex-shrink-0">
        <OpportunityStatistics
          statistics={statistics}
          isLoading={statisticsLoading && !statistics}
        />
      </div>

      <div className="flex-1 relative min-h-0">
        <div className="absolute inset-0 overflow-x-auto overflow-y-hidden">
          <KanbanBoard
            items={opportunitiesArray}
            statuses={kanbanStatuses}
            onItemClick={handleOpportunityClick}
            onStatusChange={handleStageChange}
            renderItem={(opportunity) => (
              <OpportunityKanbanCard
                opportunity={opportunity}
                visibleFieldIds={visibleFieldIds}
                customFieldNames={customFieldNames}
                quoteCustomFieldNames={quoteCustomFieldNames}
                companyCustomFieldNames={companyCustomFieldNames}
              />
            )}
            className="flex gap-3 p-3 h-full"
            columnMeta={columnMeta}
            onLoadMore={handleLoadMore}
            onDragActiveChange={handleDragActiveChange}
          />
        </div>
      </div>

      <OpportunityDetailSheet
        open={isCreateSheetOpen}
        onOpenChange={setIsCreateSheetOpen}
        onSave={handleSaveCreate}
        mode="create"
      />

      {editingOpportunityId && (
        <OpportunityDetailSheet
          open={!!editingOpportunityId}
          onOpenChange={handleEditSheetOpenChange}
          opportunityId={editingOpportunityId}
          onSave={handleSaveEdit}
          onArchive={handleArchive}
          onUnarchive={handleUnarchive}
          mode="edit"
          initialEditStageId={
            stageEditTarget?.opportunityId === editingOpportunityId
              ? stageEditTarget.initialEditStageId
              : undefined
          }
        />
      )}
    </div>
  );
}
